import { Injectable } from "@angular/core";
import { IReopenAvailableType, IReopenReason } from "@src/app/shared/models/card-actions.model";
import { CreditPaymentDataFromServer } from "@src/app/shared/models/credit-payment.model";
import { Investment, ProductCloseDataFromServer, ProductsFromServer } from "@src/app/shared/models/products.model";

import { forkJoin, Observable, of } from "rxjs";

import { catchError, map, switchMap } from "rxjs/operators";
import { ApiService } from "../api/api.service";
import { ArrestService } from "../arrests/arrest.service";
import { InvestmentApiService } from "../investment-api/investment-api.service";
import { NewCardBranch } from "../new-products/new-cards/new-card.model";
import { DocumentSettingsFromServer, SuccessfulPaymentCreation } from "../payments/payment.model";
import { PublicDataService } from "../public-data/public-data.service";
import { GeneralUtilService } from "../utils/general-util.service";

@Injectable({
  providedIn: 'root'
})
export class ProductsApiService {
  constructor(
    private apiService: ApiService,
    private investmentApiService: InvestmentApiService,
    private publicDataService: PublicDataService,
    private arrestService: ArrestService
  ) {}

  /**
   * Закрытие счета
   */
  public accountClose(body: any): Observable<any> {
    return this.apiService.post(`rest/personal/request/process/close_account`, body);
  }

  /**
   * Закрытие карты
   */
  public cardClose(body: any): Observable<any> {
    return this.apiService.post(`rest/personal/request/process/close_card`, body);
  }

  /**
   * Изменение PIN-кода карты
   */
  public changeCardPin(data: {
    cardId: string;
    encryptedPin: string;
  }): Observable<{ bpcResponseCode: string; bpcMessage: string }> {
    return this.apiService.post('rest/personal/crypto/change/pin', data);
  }

  /**
   * Изменение псевдонима продукта
   */
  public changeProductAlias(id: string, alias: string): Observable<boolean> {
    return this.apiService.put('rest/personal/product/change_alias', { id, alias });
  }

  /**
   * Проверка на брокерский счет
   */
  public checkOnBrokerAccount(number: string): boolean {
    return /^3060(1|6)/.test(number);
  }

  /**
   * Создание платежа по кредиту
   */
  public createCreditPayment(data: any): Observable<CreditPaymentDataFromServer | SuccessfulPaymentCreation> {
    return this.apiService.post('rest/personal/document/create/doc_pay_credit', data);
  }

  /**
   * Закрытие вклада
   */
  public depositClose(body: any): Observable<any> {
    return this.apiService.post(`rest/personal/request/process/close_contr`, body);
  }

  /**
   * Получение данных для закрытия счета
   */
  public getAccountCloseData(mode: string): Observable<ProductCloseDataFromServer> {
    return this.apiService.get(`rest/personal/request/process/close_account/info/${mode}`);
  }

  /**
   * Получение отделений банка
   */
  public getBranches(): Observable<NewCardBranch[]> {
    return this.apiService
      .get('rest/personal/request/process/fl_request_open_card/info/branches')
      .pipe(map((res: { bankBranch: NewCardBranch[] }) => GeneralUtilService.getArray(res.bankBranch)));
  }

  /**
   * Получение данных для закрытия карты
   */
  public getCardCloseData(mode: string): Observable<ProductCloseDataFromServer> {
    return this.apiService.get(`rest/personal/request/process/close_card/info/${mode}`);
  }

  /**
   * Получение сертификата для смены PIN-кода
   */
  public getCertificateForChangePin(): Observable<string> {
    return this.apiService.get('rest/personal/crypto/certificate/pin').pipe(
      map((res: any) => {
        return res.publicKey;
      })
    );
  }

  /**
   * Получение данных для закрытия депозита
   */
  public getDepositCloseData(mode: string): Observable<ProductCloseDataFromServer> {
    return this.apiService.get(`rest/personal/request/process/close_contr/info/${mode}`);
  }

  /**
   * Получение причин переоформления карты
   */
  public getReopenCardReasons(codeMain: string, codeAdd: string): Observable<IReopenReason[]> {
    return this.apiService.get('rest/personal/reopen_card_reason', {
      params: {
        codeMain,
        codeAdd
      }
    });
  }

  /**
   * Получение короткой карточки продукта в BackOffice
   */
  public getShortProductCardInBackOffice(branchID?: number | string): Observable<any[]> {
    return this.apiService.get(`rest/personal/online_credit/accounts?branchId=${branchID}`).pipe(
      catchError((err) => of([])),
      map((res: any[]) => {
        if (!res || res.length === 0) return [];

        return res.map((acc: any) => {
          const productCard = {
            id: acc.accId,
            icon: 'account',
            info: acc.accDisplay,
            accID: acc.accId,
            typeCaption: 'Текущий',
            currency: acc.accCurrIso,
            accNumber: acc.accNumber,
            balance: `${acc.restAmount}`,
            currencyCode: acc.accCurrIso,
            type: 'account',
            name: acc.accDisplay,
            number: acc.accNumber,
            numberForCardFilter: acc.accDisplay,
            cardType: 'account',
            creditInformation: acc.creditInformation
          };

          return productCard;
        });
      })
    );
  }

  /**
   * Загрузка доступных типов для переоформления карты по коду продукта
   */
  public loadAvailableTypesForReopenCardByProductCode(code: string): Observable<IReopenAvailableType[]> {
    return this.apiService.get('rest/personal/prog_card/dic', {
      params: {
        code
      }
    });
  }

  /**
   * Загрузка продуктов с сервера
   */
  public loadProducts(): Observable<ProductsFromServer> {
    return forkJoin([
      this.investmentApiService.getClientInfo(true),
      this.apiService.get('rest/personal/our/product?sync=true&with_blocked=true&with_stay=true'),
      this.publicDataService.getServiceData('currency', true)
    ]).pipe(
      switchMap(([investment, products, currencies]: [Investment, ProductsFromServer, any]) => {
        let accounts = [];

        if (products.accounts && typeof products.accounts === 'object' && !Array.isArray(products.accounts)) {
          accounts = [{ ...products.accounts }];
        } else if (products.accounts && Array.isArray(products.accounts)) {
          accounts = [...products.accounts];
        }

        const originalAccounts = [...accounts];

        products.accounts = [...accounts];
        // Фильтруем накопительные счета
        products.saveAccounts = products.accounts
          .filter((i) => i.subtype === '15')
          .map((account) => {
            return {
              ...account,
              tariff: this.extractTariffPlanFromAccountInfo(account.info)
            };
          });
        // Фильтруем металл счета
        products.metallAccounts = products.accounts.filter((i) => {
          return this.isMetallAccount(i);
        });
        // Фильтруем все остальные счета
        products.accounts = products.accounts.filter((i) => !['14', '15'].includes(i.subtype));

        // Обрабатываем данные об арестах (всегда вызываем setStays для пометки загрузки)
        const stays = products.stays ? GeneralUtilService.getArray(products.stays) : [];

        this.arrestService.setStays(stays);

        if (investment) {
          const brokerAccounts = originalAccounts.filter((account) => this.checkOnBrokerAccount(account.number));
        }

        return of(products);
      })
    );
  }

  /**
   * Загрузка данных для переоформления карты
   */
  public loadReopenCardData(data?: any): Observable<DocumentSettingsFromServer | SuccessfulPaymentCreation> {
    return this.apiService.post(`rest/personal/request/process/fl_request_reopen_card`, data);
  }

  /**
   * Блокировка карты
   */
  public lockCard(data: any): Observable<DocumentSettingsFromServer | SuccessfulPaymentCreation> {
    return this.apiService.post(`rest/personal/request/process/lock_card_request`, data);
  }

  /**
   * Установка счета по умолчанию
   */
  public setDefaultAccount(data: any): Observable<any> {
    return this.apiService.post('rest/personal/v1/set_default_acc', data).pipe(map((res: any) => res.id));
  }

  /**
   * Разблокировка карты
   */
  public unlockCard(data: any): Observable<DocumentSettingsFromServer | SuccessfulPaymentCreation> {
    return this.apiService.post(`rest/personal/request/process/unlock_card_request`, data);
  }

  /**
   * Извлечение тарифного плана из информации об аккаунте
   */
  private extractTariffPlanFromAccountInfo(info: string): string {
    // Реализация метода
    return info;
  }

  /**
   * Проверка на металлический счет
   */
  private isMetallAccount(account: any): boolean {
    return (
      ['978', '840', '756', '826'].indexOf(account.currencyCode) === -1 &&
      account.currency !== 'RUR' &&
      !this.checkOnBrokerAccount(account.number)
    );
  }
}
